import type React from 'react';
import { useCallback } from 'react';

import { useScrollFieldIntoView } from 'src/components';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { useWalkthrough } from 'src/editor/toolbars';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import {
  useFieldFromCache,
  useGetCurrentDoc,
  useGetEditorUserState,
  useGetIsFieldInDocument,
  useIsAuthorizedToEditStorable,
} from 'src/hooks';
import type { ActionWithTimestamps } from 'src/types';
import { getWorkflowWasSentForSig } from 'src/workflow';

/**
 * Handles clicking next on field popovers.
 */
export function useOnClickNextField(): {
  onClickNext: (
    event: React.SyntheticEvent,
    field: Pick<Apollo.Field, 'nodeId' | '__typename'>
  ) => Promise<void>;
} {
  const { scrollFieldIntoView } = useScrollFieldIntoView();
  const { advanceWalkthrough, walkThroughActive } = useWalkthrough();
  const onClickNext = useCallback(
    async (event: React.SyntheticEvent, field: Pick<Apollo.Field, 'nodeId' | '__typename'>) => {
      if (walkThroughActive) return advanceWalkthrough(event);

      scrollFieldIntoView(field);
    },
    [advanceWalkthrough, scrollFieldIntoView, walkThroughActive]
  );

  return { onClickNext };
}

/**
 * Used by popover components to get field details for rendering popover.
 */
export function useGetFieldDetails(fieldId: UUID): {
  disabledReason: string;
  document: ReturnType<typeof useGetCurrentDoc>['document'];
  field: ReturnType<typeof useFieldFromCache>['field'];
  loading: boolean;
  showCompletedPopover: boolean;
} {
  const { field, loading } = useFieldFromCache(fieldId);
  const { disabledReason, document } = useGetFieldDisabledReason(field);
  const workflowSentForSig = getWorkflowWasSentForSig(document?.workflow ?? null);
  const showCompletedPopover = workflowSentForSig && Boolean(field?.action?.completedAt);

  return {
    disabledReason,
    document,
    field,
    loading,
    showCompletedPopover,
  };
}

interface GetFieldDisabledVariable extends Pick<
  Apollo.DocumentVariable,
  'id' | 'isField' | 'nodeId' | '__typename'
> {
  action: ActionWithTimestamps | null;
}
interface GetFieldDisabledSignature extends Pick<
  Apollo.SignatureField,
  'id' | 'nodeId' | '__typename'
> {
  action: ActionWithTimestamps | null;
}
type GetFieldDisabledField = GetFieldDisabledVariable | GetFieldDisabledSignature;

export function getFieldDisabledReason(options: {
  canEdit: boolean;
  currentUserId: UUID | null | undefined;
  document:
    | {
        variant?: Apollo.DocumentVariant | null;
        workflow?: Pick<Apollo.Workflow, 'state' | '__typename'> | null;
      }
    | null
    | undefined;
  field: GetFieldDisabledField | null;
  isFieldInDocument: boolean;
  isReadOnly: boolean;
}): string {
  const { canEdit, currentUserId, document, field, isFieldInDocument, isReadOnly } = options;
  const isTemplate = document?.variant === Apollo.DocumentVariant.Template;
  const workflowSentForSig = getWorkflowWasSentForSig(document?.workflow ?? null);

  // Fields are *not* disabled in templates so everyone can "test" filling out the field.
  if (isTemplate) return !canEdit ? 'Not authorized to edit document' : '';

  // It's a contract so we need to check whether the current user is authorized to complete.
  if (!currentUserId || !field) return 'Checking authorization...';

  if (isFieldInDocument && isReadOnly) return 'Field is read only';

  // Unassigned fields can be completed if the user is an Editor.
  if (!field.action?.assignee) return !canEdit ? 'Not authorized to edit document' : '';

  const userIsAssignee = field.action.assignee.id === currentUserId;
  const isSignatureField = field.__typename === 'SignatureField';
  const canFillPreSigning = document != null && !workflowSentForSig && !isSignatureField;
  if (!userIsAssignee) {
    if (!canEdit) return 'Not authorized to edit document';
    if (!canFillPreSigning) return 'Not assigned to field';
  }

  const isSigActionNotStarted = isSignatureField && Boolean(!field.action.startedAt);
  if (isSigActionNotStarted) return 'Workflow step for signature not started yet';

  // If workflow hasn't been sent for signature, don't prevent users from changing field values...
  const isActionCompleted = workflowSentForSig && Boolean(field.action.completedAt);
  if (isActionCompleted) return 'Workflow action completed';

  return '';
}

export function useGetFieldDisabledReason(field: GetFieldDisabledField | null): {
  disabledReason: string;
  document: ReturnType<typeof useGetCurrentDoc>['document'];
} {
  const { document } = useGetCurrentDoc();
  const currentUserId = useCurrentUserId();
  const { documentId } = useDocumentParams();
  const { isReadOnly } = useGetEditorUserState();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const { getIsFieldInDocument } = useGetIsFieldInDocument(tabId);
  const { canEdit } = useIsAuthorizedToEditStorable(documentId);

  return {
    disabledReason: getFieldDisabledReason({
      canEdit,
      currentUserId,
      document,
      field,
      isFieldInDocument: field ? getIsFieldInDocument(field) : false,
      isReadOnly,
    }),
    document,
  };
}
